/**
 * orgs-page.tsx — Danh sách tổ chức (Platform). Route `/platform/companies`.
 * Bảng: tên, trạng thái, gói, ngày hết hạn, số user. Tìm kiếm + "Vào công ty".
 * Chế độ "Đã xoá": tổ chức xoá mềm chờ xoá vĩnh viễn (ngày xoá, ngày tự xoá) — bấm vào để khôi phục / xoá vĩnh viễn.
 */
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Search, LogIn, Plus, Loader2, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/shared/page-header'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { formatNumber } from '@/lib/utils'
import { platformApiError } from '@/lib/platform-client'
import {
  usePlatformOrgs,
  useEnterCompany,
  useCreateOrg,
  type OrgListItem,
} from '@/hooks/use-platform'
import { OrgStatusBadge, formatDate } from './org-status'
import { usePlatformPricing } from '@/hooks/use-platform-pricing'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select'
import { apiErrorCode, endOfDayVn, expiryDateError, fieldForApiError, planRequiresExpiry, type OrgFormField } from './plan-expiry'
import { ReasonDialog } from './org-detail/reason-dialog'

const PAGE_SIZE = 20

export function OrgsPage() {
  const navigate = useNavigate()
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [createOpen, setCreateOpen] = useState(false)
  const [params, setParams] = useSearchParams()
  const view: 'active' | 'deleted' = params.get('view') === 'deleted' ? 'deleted' : 'active'
  const setView = (v: 'active' | 'deleted') => {
    const next = new URLSearchParams(params)
    if (v === 'deleted') next.set('view', 'deleted')
    else next.delete('view')
    setParams(next, { replace: true })
  }

  const { data, isLoading, isFetching } = usePlatformOrgs({
    search, page, pageSize: PAGE_SIZE, ...(view === 'deleted' ? { deleted: '1' as const } : {}),
  })
  const enterCompany = useEnterCompany()
  // vbsec M9: "vào công ty" bắt buộc lý do (ghi nhật ký Platform) — hỏi qua ReasonDialog trước khi gọi API.
  const [enterTarget, setEnterTarget] = useState<OrgListItem | null>(null)

  function onSearch(e: React.FormEvent) {
    e.preventDefault()
    setPage(1)
    setSearch(searchInput.trim())
  }

  async function handleEnter(reason: string) {
    if (!enterTarget) return
    try {
      await enterCompany.mutateAsync({ orgId: enterTarget.id, reason })
      toast.success(`Đang mở CRM của "${enterTarget.name}"...`)
      // Full reload để CRM nạp lại token company vừa ghi.
      window.location.href = '/conversations'
    } catch (err) {
      toast.error(platformApiError(err))
    }
  }

  const deletedColumns: Column<OrgListItem>[] = [
    {
      key: 'name',
      header: 'Tổ chức',
      cell: (o) => (
        <div>
          <p className="font-medium">{o.name}</p>
          <p className="text-xs text-muted-foreground">Tạo ngày {formatDate(o.createdAt)}</p>
        </div>
      ),
    },
    { key: 'deletedAt', header: 'Ngày xoá', cell: (o) => formatDate(o.deletedAt ?? null) },
    {
      key: 'purgeAt',
      header: 'Tự xoá vĩnh viễn',
      cell: (o) => (o.purgeAt ? formatDate(o.purgeAt) : <span className="text-muted-foreground">Không tự xoá</span>),
    },
    { key: 'users', header: 'Số user', align: 'right', cell: (o) => formatNumber(o.stats.users) },
  ]

  const columns: Column<OrgListItem>[] = [
    {
      key: 'name',
      header: 'Tổ chức',
      cell: (o) => (
        <div>
          <p className="font-medium">{o.name}</p>
          <p className="text-xs text-muted-foreground">Tạo ngày {formatDate(o.createdAt)}</p>
        </div>
      ),
    },
    { key: 'status', header: 'Trạng thái', cell: (o) => <OrgStatusBadge status={o.displayStatus} /> },
    { key: 'plan', header: 'Gói', cell: (o) => o.plan || <span className="text-muted-foreground">—</span> },
    {
      key: 'expiresAt',
      header: 'Hết hạn',
      cell: (o) => (
        <span className={o.expiringSoon ? 'font-medium text-warning' : undefined}>
          {formatDate(o.expiresAt)}
        </span>
      ),
    },
    { key: 'users', header: 'Số user', align: 'right', cell: (o) => formatNumber(o.stats.users) },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (o) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              setEnterTarget(o)
            }}
            disabled={enterCompany.isPending}
          >
            <LogIn className="h-4 w-4" /> Vào công ty
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Tổ chức"
        description="Quản lý toàn bộ tổ chức trên hệ thống."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> Tạo tổ chức
          </Button>
        }
      />

      <Tabs value={view} onValueChange={(v) => { setView(v as 'active' | 'deleted'); setPage(1) }}>
        <TabsList>
          <TabsTrigger value="active">Đang dùng</TabsTrigger>
          <TabsTrigger value="deleted">
            <Trash2 className="h-3.5 w-3.5" /> Đã xoá{data?.meta.deletedCount ? ` (${formatNumber(data.meta.deletedCount)})` : ''}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <form onSubmit={onSearch} className="flex gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Tìm theo tên tổ chức..."
            className="pl-9"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <Button type="submit" variant="outline">
          Tìm kiếm
        </Button>
      </form>

      {view === 'deleted' && (
        <p className="text-sm text-muted-foreground">
          Tổ chức đã xoá bị khoá đăng nhập và khoá kênh. Bấm vào một tổ chức để khôi phục hoặc xoá vĩnh viễn.
        </p>
      )}

      <DataTable
        columns={view === 'deleted' ? deletedColumns : columns}
        rows={data?.items ?? []}
        loading={isLoading}
        rowKey={(o) => o.id}
        emptyTitle={view === 'deleted' ? 'Không có tổ chức nào đã xoá' : 'Chưa có tổ chức nào'}
        onRowClick={(o) => navigate(`/platform/companies/${o.id}`)}
      />

      {data && data.meta.total > 0 && (
        <div className="flex items-center justify-between">
          {isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <div className="ml-auto">
            <Pagination
              page={page}
              limit={PAGE_SIZE}
              total={data.meta.total}
              onPageChange={setPage}
            />
          </div>
        </div>
      )}

      <CreateOrgDialog open={createOpen} onOpenChange={setCreateOpen} />

      {enterTarget && (
        <ReasonDialog
          open onOpenChange={(v) => !v && setEnterTarget(null)}
          title={`Vào công ty — ${enterTarget.name}`}
          description="Đăng nhập thay bằng tài khoản chủ sở hữu/quản trị của tổ chức này — toàn quyền trên dữ liệu khách hàng. Lý do được ghi vào nhật ký quản trị Platform."
          confirmLabel="Vào công ty"
          pending={enterCompany.isPending}
          onConfirm={handleEnter}
        />
      )}
    </div>
  )
}

/** Giá trị điền sẵn (vd tạo tổ chức từ lead Tiếp thị liên kết). */
export interface CreateOrgInitial {
  orgName?: string
  ownerFullName?: string
  ownerEmail?: string
  referralCode?: string
  leadId?: string
}

export function CreateOrgDialog({ open, onOpenChange, initial, onCreated }: {
  open: boolean
  onOpenChange: (v: boolean) => void
  initial?: CreateOrgInitial
  onCreated?: () => void
}) {
  const createOrg = useCreateOrg()
  const pricing = usePlatformPricing()
  const [form, setForm] = useState({
    orgName: '',
    ownerFullName: '',
    ownerEmail: '',
    ownerPassword: '',
    plan: '',
    expiresAt: '',
    referralCode: '',
  })
  // Điền sẵn khi mở từ lead.
  useEffect(() => {
    if (open && initial) {
      setForm((f) => ({
        ...f,
        orgName: initial.orgName ?? f.orgName,
        ownerFullName: initial.ownerFullName ?? f.ownerFullName,
        ownerEmail: initial.ownerEmail ?? f.ownerEmail,
        referralCode: initial.referralCode ?? f.referralCode,
      }))
    }
  }, [open, initial])

  // Lỗi báo ngay tại ô (Q1: mã giới thiệu sai; Q4: thiếu hạn gói trả phí). Dữ liệu form giữ nguyên khi lỗi.
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<OrgFormField, string>>>({})
  const selectedPlan = (pricing.data?.pricing.plans ?? []).find((p) => p.id === form.plan) ?? null
  const expiryRequired = planRequiresExpiry(selectedPlan)

  function set<K extends keyof typeof form>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }))
    if (k === 'referralCode' || k === 'expiresAt' || k === 'plan') {
      setFieldErrors((fe) => ({ ...fe, [k === 'plan' ? 'expiresAt' : k]: undefined }))
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const expiryError = expiryDateError(form.expiresAt, expiryRequired)
    if (expiryError) {
      setFieldErrors((fe) => ({ ...fe, expiresAt: expiryError }))
      return
    }
    try {
      const created = await createOrg.mutateAsync({
        orgName: form.orgName.trim(),
        ownerFullName: form.ownerFullName.trim(),
        ownerEmail: form.ownerEmail.trim(),
        ownerPassword: form.ownerPassword,
        plan: form.plan.trim() || null,
        // Hết hạn vào CUỐI ngày đã chọn (giờ Việt Nam).
        expiresAt: form.expiresAt ? endOfDayVn(form.expiresAt) : null,
        referralCode: form.referralCode.trim() || null,
        leadId: initial?.leadId ?? null,
      })
      // E20: tổ chức vẫn tạo nhưng mã người giới thiệu không gắn được → cảnh báo rõ lý do.
      const referralError = (created as { referralError?: string | null } | undefined)?.referralError
      if (referralError) toast.warning(`Đã tạo tổ chức nhưng chưa gắn được mã người giới thiệu: ${referralError}`)
      else toast.success('Đã tạo tổ chức mới')
      onOpenChange(false)
      onCreated?.()
      setFieldErrors({})
      setForm({ orgName: '', ownerFullName: '', ownerEmail: '', ownerPassword: '', plan: '', expiresAt: '', referralCode: '' })
    } catch (err) {
      const message = platformApiError(err)
      const field = fieldForApiError(apiErrorCode(err))
      // Q1: tổ chức KHÔNG được tạo — giữ nguyên form, báo lỗi ngay tại ô để sửa / xoá mã rồi tạo lại.
      if (field) setFieldErrors((fe) => ({ ...fe, [field]: message }))
      toast.error(message)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tạo tổ chức mới</DialogTitle>
          <DialogDescription>Tạo tổ chức kèm tài khoản chủ sở hữu đầu tiên.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="orgName">Tên tổ chức</Label>
            <Input id="orgName" value={form.orgName} onChange={(e) => set('orgName', e.target.value)} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ownerFullName">Tên chủ sở hữu</Label>
              <Input id="ownerFullName" value={form.ownerFullName} onChange={(e) => set('ownerFullName', e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ownerEmail">Email chủ sở hữu</Label>
              <Input id="ownerEmail" type="email" value={form.ownerEmail} onChange={(e) => set('ownerEmail', e.target.value)} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ownerPassword">Mật khẩu (≥ 8 ký tự)</Label>
              <Input id="ownerPassword" type="password" value={form.ownerPassword} onChange={(e) => set('ownerPassword', e.target.value)} required minLength={8} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="plan">Gói (theo bảng giá)</Label>
              <Select value={form.plan || 'none'} onValueChange={(v) => set('plan', v === 'none' ? '' : v)}>
                <SelectTrigger id="plan">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Mặc định (Miễn phí)</SelectItem>
                  {(pricing.data?.pricing.plans ?? []).map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="expiresAt">
              {expiryRequired ? 'Ngày hết hạn (bắt buộc với gói trả phí)' : 'Ngày hết hạn (bỏ trống = không giới hạn)'}
            </Label>
            <Input
              id="expiresAt" type="date" value={form.expiresAt} onChange={(e) => set('expiresAt', e.target.value)}
              required={expiryRequired} aria-invalid={!!fieldErrors.expiresAt}
              aria-describedby={fieldErrors.expiresAt ? 'expiresAt-error' : undefined}
            />
            {fieldErrors.expiresAt && <p id="expiresAt-error" className="text-xs text-destructive">{fieldErrors.expiresAt}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="referralCode">Mã người giới thiệu (tuỳ chọn)</Label>
            <Input
              id="referralCode" value={form.referralCode} onChange={(e) => set('referralCode', e.target.value.toUpperCase())}
              placeholder="Mã đối tác tiếp thị liên kết" maxLength={20} aria-invalid={!!fieldErrors.referralCode}
              aria-describedby={fieldErrors.referralCode ? 'referralCode-error' : undefined}
            />
            {fieldErrors.referralCode && (
              <p id="referralCode-error" className="text-xs text-destructive">
                {fieldErrors.referralCode}. Sửa lại mã hoặc xoá mã rồi bấm “Tạo tổ chức”.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Hủy
            </Button>
            <Button type="submit" disabled={createOrg.isPending}>
              {createOrg.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Tạo tổ chức
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
